import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { imagePath } from '../../utils/imagePath';
import { navLinks } from '../../utils/constant';
import { RightIcon } from '../ui/UtilsLayout';
import { motion, AnimatePresence } from 'framer-motion';
import { toggleChatInterface } from '../../redux/Slice/homeSlice';
import store from '../../redux/store';

const Sidebar = React.memo(({ className, close, setIsTourOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize open state with empty object
  const [open, setOpen] = useState({});

  // Auto-expand menus based on current route
  useEffect(() => {
    const expandParentMenus = () => {
      const newOpenState = {};

      navLinks.forEach((item) => {
        // Check if this main item's path matches current path
        if (
          location.pathname.includes(item.link) &&
          item.link !== '/dashboard'
        ) {
          newOpenState[item.name] = true;
        }

        // Check if the location includes any submenu paths
        if (item.submenu) {
          item.submenu.forEach((sub) => {
            if (location.pathname.includes(`${sub.path}/${sub.category}`)) {
              newOpenState[item.name] = true;

              // If there's a nested submenu, check that too
              if (sub.submenu) {
                sub.submenu.forEach((nested) => {
                  if (
                    location.pathname.includes(
                      `${nested.path}/${nested.category}`
                    )
                  ) {
                    newOpenState[sub.name || sub.type] = true;
                  }
                });
              }
            }
          });
        }
      });

      // Update only the menus that need to be open
      setOpen((prev) => ({
        ...prev,
        ...newOpenState,
      }));
    };

    expandParentMenus();
  }, [location.pathname]);

  const toggleMenu = (menuName, e) => {
    if (e) {
      e.stopPropagation(); // Prevent event bubbling
    }

    setOpen((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };

  const handleClose = () => {
    if (close) {
      close();
    }
    // Don't reset open state when closing sidebar
  };

  return (
    <div
      className={`custom-scrollbar  h-[calc(100vh-68px)] min-h-full overflow-auto bg-[#1e1f22] relative z-[9] flex flex-col shadow-md shadow-[rgba(0,0,0,0.4)] justify-between md:pr-4 ${className}`}
    >
      {/* Navigation Links */}
      <div className="flex flex-col gap-2 p-3">
        {navLinks.map((item, index) => {
          const isActive =
            location.pathname === item.link ||
            (location.pathname.includes(item.link) &&
              item.link !== '/dashboard') ||
            location.pathname === `${item.link}${item.id ? `/${item.id}` : ''}`;

          return (
            <div
              key={index}
              onClick={() => {
                if (item.link == '/dashboard') {
                  store.dispatch(toggleChatInterface(false));
                }
              }}
            >
              {/* Main Menu Item */}
              <div
                className={` flex items-center justify-between gap-6 px-4 py-1 rounded-md cursor-pointer text-xl transition-all duration-300
                ${
                  isActive
                    ? 'bg-[#333334] text-white'
                    : 'text-[#979797] hover:bg-[#333334] hover:text-white'
                }
                `}
                onClick={() => {
                  navigate(`${item.link}${item.id ? `/${item.id}` : ''}`);

                  handleClose();
                }}
              >
                <div className="flex items-center gap-4 py-1">
                  <img
                    className="h-[16px] w-[16px] text-[#979797]"
                    src={item.icon}
                    alt={item.name}
                  />
                  <span className="text-sm font-medium">{item.name}</span>
                </div>

                {/* Check if submenu exists */}
                {item?.submenu && (
                  <RightIcon
                    className="block lg:hidden"
                    onClick={(e) => toggleMenu(item.name, e)}
                    open={open[item.name]}
                  />
                )}
              </div>

              {/* First-Level Submenu */}
              {item?.submenu && (
                <AnimatePresence>
                  {open[item.name] && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="flex flex-col lg:hidden"
                    >
                      {item.submenu.map((sub, subIndex) => {
                        const isSubActive =
                          location.pathname === `${sub.path}/${sub.category}`;
                        return (
                          <div key={subIndex}>
                            <div
                              onClick={(e) => {
                                if (sub.submenu) {
                                  toggleMenu(sub.name || sub.type, e);
                                } else {
                                  navigate(`${sub.path}/${sub.category}`);
                                  handleClose();
                                }
                              }}
                              className={`flex items-center justify-between gap-4 pl-4 px-4 py-1 rounded-md cursor-pointer text-sm transition-all duration-300
                              ${
                                isSubActive
                                  ? 'bg-[#333334] text-white'
                                  : 'text-[#979797] hover:bg-[#333334] hover:text-white'
                              }`}
                            >
                              <span>{sub?.name || sub?.type}</span>

                              {/* Nested submenu check */}
                              {sub?.submenu && (
                                <RightIcon
                                  className="block lg:hidden"
                                  onClick={(e) =>
                                    toggleMenu(sub.name || sub.type, e)
                                  }
                                  open={open[sub.name || sub.type]}
                                />
                              )}
                            </div>

                            {/* Second-Level Submenu */}
                            {sub?.submenu && (
                              <AnimatePresence>
                                {open[sub.name || sub.type] && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{
                                      duration: 0.3,
                                      ease: 'easeInOut',
                                    }}
                                    className="flex flex-col lg:hidden"
                                  >
                                    {sub.submenu.map((nested, nestedIndex) => (
                                      <div
                                        key={nestedIndex}
                                        onClick={() => {
                                          navigate(
                                            `${nested.path}/${nested.category}`
                                          );
                                          handleClose();
                                        }}
                                        className={`flex items-center gap-4 pl-4 px-3 py-1 rounded-md cursor-pointer text-sm transition-all duration-300
                                        ${
                                          location.pathname ===
                                          `${nested.path}/${nested.category}`
                                            ? 'bg-[#333334] text-white'
                                            : 'text-[#979797] hover:bg-[#333334] hover:text-white'
                                        }`}
                                      >
                                        {nested?.name || nested?.type}
                                      </div>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            )}
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom Section */}
      <div className="p-4 border-t border-[#393939] mt-auto">
        <a
          href="https://discord.gg/JnGE45yzH"
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <div className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-[#2a2b2d] cursor-pointer">
            <img
              className="h-[16px] w-[16px]"
              src={imagePath?.layer2Image}
              alt="Discord"
            />
            <span className="text-[12px] text-[#6F7BFD]">
              Join our Discord
            </span>
          </div>
        </a>
        <div className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-[#2a2b2d] cursor-pointer">
          <div className="h-[16px] w-[16px] bg-[#565759] rounded-full flex items-center justify-center">
            <img
              className="h-[9px] w-[9px]"
              src={imagePath?.helpImage}
              alt="Help"
            />
          </div>

          <span
            onClick={() => setIsTourOpen(true)}
            className="text-[12px] text-[#979797]"
          >
            Help & Feedback
          </span>
        </div>
      </div>
    </div>
  );
});

export default Sidebar;
